import {
  Alert,
  CostInsightsApi,
  DEFAULT_DATE_FORMAT,
  ProductInsightsOptions,
} from '@backstage-community/plugin-cost-insights';
import {
  Cost,
  DateAggregation,
  Duration,
  Entity,
  Group,
  MetricData,
  Project,
} from '@tranzact/backstage-plugin-cost-insightz';

import { DateTime } from 'luxon';

import {
  GetCostAndUsageCommandOutput,
  GetTagsCommandOutput,
  ResultByTime,
} from '@aws-sdk/client-cost-explorer';

import {
  aggregationFor,
  changeOf,
  inclusiveStartDateOf,
  trendlineOf,
} from '@tranzact/backstage-plugin-cost-insightz';

import { ConfigApi } from '@backstage/core-plugin-api';
import axios from 'axios';
import { format } from 'date-fns';

let token: string = '';
let configAxios = {};

export class CostInsightsClient implements CostInsightsApi {
  private backendUrl: string;

  constructor(config: ConfigApi) {
    if (!config) {
      throw new Error('Config is required');
    }
    this.backendUrl = `${config.getString('backend.baseUrl')}/api`;
  }

  private request(_: any, res: any): Promise<any> {
    return new Promise(resolve => setTimeout(resolve, 0, res));
  }

  async getLastCompleteBillingDate(): Promise<string> {
    return Promise.resolve(
      DateTime.now().minus({ days: 1 }).toFormat(DEFAULT_DATE_FORMAT),
    );
  }

  async getUserGroups(userId: string): Promise<Group[]> {
    const groups: Group[] = await this.request({ userId }, [
      { id: 'tranzact', name: 'tranzact' },
    ]);

    token = localStorage.getItem('token')!;
    configAxios = {
      headers: { Authorization: `Bearer ${token}` },
    };

    return groups;
  }

  async getGroupProjects(_group: string): Promise<Project[]> {
    // console.log({ group });
    const projects: Project[] = (await this.getTagsCostExplorer()).Tags!.map(
      tag => {
        return { id: tag, name: tag };
      },
    );

    return projects;
  }

  async getAlerts(_group: string): Promise<Alert[]> {
    return [];
  }

  // Linear graph of the total number of services used per day
  async getDailyMetricData(
    metric: string,
    intervals: string,
  ): Promise<MetricData> {
    const endDate = intervals.split('/')[2];
    const duration: Duration = Object.values(Duration).find(
      value => value === intervals.split('/')[1],
    ) as Duration | Duration.P90D;
    const startDate = inclusiveStartDateOf(duration, endDate);

    const data = await this.getDailyMetricByServiceCostExplorer(
      startDate,
      endDate,
    );

    const aggregation: DateAggregation[] = data.ResultsByTime!.map(item => {
      const date = item.TimePeriod!.Start!.slice(0, 10);
      const amount = item.Groups!.reduce((acc, group) => {
        if (parseFloat(group.Metrics!.UnblendedCost.Amount!) > 0.0) {
          return acc + 1;
        }
        return acc;
      }, 0);
      return { date, amount };
    });

    const cost: MetricData = await this.request(
      { metric, intervals },
      {
        format: 'number',
        aggregation: aggregation,
        change: changeOf(aggregation),
        trendline: trendlineOf(aggregation),
      },
    );

    return cost;
  }

  // Area chart showing total cost per day of services in aws
  // Area chart showing total cost per day grouped by service
  async getGroupDailyCost(group: string, intervals: string): Promise<Cost> {
    const endDate = intervals.split('/')[2];
    const duration: Duration = Object.values(Duration).find(
      value => value === intervals.split('/')[1],
    ) as Duration | Duration.P90D;
    const startDate = inclusiveStartDateOf(duration, endDate);

    const data = await this.getDailyMetricGroupCostExplorer(startDate, endDate);
    const dataServices = await this.getDailyMetricByServiceCostExplorer(
      startDate,
      endDate,
    );

    const aggregation: DateAggregation[] = this.transformDataAggregation(data);

    const aggregationByService = this.transformDataServicesAggregation(
      dataServices,
      aggregation,
    );

    const groupDailyCost: Cost = await this.request(
      { group, intervals },
      {
        aggregation: aggregation,
        change: changeOf(aggregation),
        trendline: trendlineOf(aggregation),
        // Optional field providing cost groupings / breakdowns keyed by the type. In this example,
        // daily cost grouped by cloud service.
        groupedCosts: {
          service: aggregationByService,
        },
      },
    );

    return groupDailyCost;
  }

  private transformDataServicesAggregation(
    dataServices: GetCostAndUsageCommandOutput,
    aggregation: DateAggregation[],
  ) {
    const resultByService: {
      id: string;
      aggregation: DateAggregation[];
    }[] = [];

    resultByService.push({
      id: 'Other',
      aggregation: [],
    });

    dataServices.ResultsByTime!.forEach(item => {
      let totalCostGroup: number = 0.0;
      const date = item.TimePeriod!.Start!.slice(0, 10);
      item.Groups!.forEach(group => {
        totalCostGroup += parseFloat(
          group.Metrics!.UnblendedCost.Amount ?? '0',
        );
        const amount = parseFloat(group.Metrics!.UnblendedCost.Amount ?? '0');

        const existingItem = resultByService.find(
          resItem => resItem.id === group.Keys![0],
        );

        if (existingItem) {
          existingItem.aggregation.push({ date, amount });
        } else {
          resultByService.push({
            id: group.Keys![0],
            aggregation: [{ date, amount }],
          });
        }
      });

      const existingOtherItem = resultByService.find(
        resItem => resItem.id === 'Other',
      );
      const totalCost: number =
        aggregation.find(resItem => resItem.date === date)?.amount ?? 0.0;

      if (existingOtherItem) {
        existingOtherItem.aggregation.push({
          date,
          amount: totalCost - totalCostGroup,
        });
      }
    });

    return resultByService;
  }

  private transformDataAggregation(data: GetCostAndUsageCommandOutput) {
    return data
      .ResultsByTime!.map(entry => {
        // Extract the TimePeriod property and convert it to a date string
        const dateString = entry.TimePeriod!.Start!.slice(0, 10);

        // Extract the Total property and calculate the amount
        const amount = Object.values(entry.Total || {}).reduce(
          (sum, current) => sum + (parseFloat(current?.Amount!) || 0),
          0,
        );

        // Return the new DateAggregation object
        return {
          date: dateString,
          amount,
        };
      })
      ?.filter(Boolean);
  }

  async getProjectDailyCost(project: string, intervals: string): Promise<Cost> {
    const endDate = intervals.split('/')[2];
    const duration: Duration = Object.values(Duration).find(
      value => value === intervals.split('/')[1],
    ) as Duration | Duration.P90D;
    const startDate = inclusiveStartDateOf(duration, endDate);

    const data = await this.getDailyMetricGroupCostExplorer(
      startDate,
      endDate,
      project,
    );
    const dataServices = await this.getDailyMetricByServiceCostExplorer(
      startDate,
      endDate,
      project,
    );

    const aggregation: DateAggregation[] = this.transformDataAggregation(data);
    const aggregationByService = this.transformDataServicesAggregation(
      dataServices,
      aggregation,
    );

    const projectDailyCost: Cost = await this.request(
      { project, intervals },
      {
        id: project,
        aggregation: aggregation,
        change: changeOf(aggregation),
        trendline: trendlineOf(aggregation),
        // Optional field providing cost groupings / breakdowns keyed by the type. In this example,
        // daily project cost grouped by cloud service.
        groupedCosts: {
          service: aggregationByService,
        },
      },
    );
    // console.log({projectDailyCost});

    return projectDailyCost;
  }

  async getCatalogEntityDailyCost(
    catalogEntityRef: string,
    intervals: string,
  ): Promise<Cost> {
    const aggregation = aggregationFor(intervals, 8_000);

    const groupDailyCost: Cost = await this.request(
      { catalogEntityRef, intervals },
      {
        aggregation: aggregation,
        change: changeOf(aggregation),
        trendline: trendlineOf(aggregation),
        // Optional field providing cost groupings / breakdowns keyed by the type. In this example,
        // daily cost grouped by cloud product OR by project / billing account.
        groupedCosts: {
          // product: getGroupedProducts(intervals),
          // project: getGroupedProjects(intervals),
        },
      },
    );
    // console.log({ groupDailyCost });
    return groupDailyCost;
  }

  async getProductInsights(options: ProductInsightsOptions): Promise<Entity> {
    if (options.product === 'computeEngine') {
      const transformedData = await this.getProductInsightsData(options);

      const productInsights: Entity = await this.request(
        options,
        transformedData,
      );

      return productInsights;
    }

    const productInsights: Entity = await this.request(options, {});

    return productInsights;
  }

  private async getProductInsightsData(options: ProductInsightsOptions) {
    const endDate = options.intervals.split('/')[2];
    const duration: Duration = Object.values(Duration).find(
      value => value === options.intervals.split('/')[1],
    ) as Duration | Duration.P90D;
    const startDate = inclusiveStartDateOf(duration, endDate);

    const data = await this.getDailyMetricGroupCostExplorer(
      startDate,
      endDate,
      options.project!,
    );
    const dataServices = await this.getDailyMetricByServiceCostExplorer(
      startDate,
      endDate,
      options.project!,
    );

    const aggregation: DateAggregation[] = this.transformDataAggregation(data);
    const aggregationByService = this.transformDataServicesAggregation(
      dataServices,
      aggregation,
    );
    const transformedData = this.transformDataProductInsight(
      dataServices.ResultsByTime!,
      duration,
      aggregationByService,
      options.product,
    );
    return transformedData;
  }

  private async getTagsCostExplorer(): Promise<GetTagsCommandOutput> {
    const endDate = format(new Date(), 'yyyy-MM-dd');
    const duration = Duration.P3M;
    const startDate = inclusiveStartDateOf(duration, endDate);

    const params = {
      MaxResults: 28,
      SortBy: [
        {
          Key: 'UnblendedCost',
          SortOrder: 'DESCENDING',
        },
      ],
      TagKey: 'Dept',
      TimePeriod: {
        End: endDate,
        Start: startDate,
      },
    };

    const response = await axios.post(
      `${this.backendUrl}/cost-explorer/tags`,
      params,
      configAxios,
    );

    const data: GetTagsCommandOutput = response.data;
    return data;
  }

  private async getDailyMetricGroupCostExplorer(
    startDate?: string,
    endDate?: string,
    dept?: string,
  ) {
    const end = format(new Date(), 'yyyy-MM-dd');
    const duration = Duration.P3M;
    const start = inclusiveStartDateOf(duration, end);

    const paramsDefault = {
      Granularity: 'DAILY',
      GroupBy: [],
      Metrics: ['UnblendedCost'],
      TimePeriod: {
        End: endDate ?? end,
        Start: startDate ?? start,
      },
    };

    const filterTag: { Tags: { Key: string; Values: string[] } }[] = [];

    dept?.split(',').map(item => {
      if (item.trim() !== 'All Departments') {
        filterTag.push({ Tags: { Key: 'Dept', Values: [item.trim()] } });
      }
    });

    const paramsWithFilter =
      filterTag.length <= 1
        ? {
            ...paramsDefault,
            Filter: {
              Tags: {
                Key: 'Dept',
                Values: [dept],
              },
            },
          }
        : {
            ...paramsDefault,
            Filter: {
              Or: filterTag,
            },
          };

    const params =
      dept === null ||
      dept === undefined ||
      dept?.split(',').indexOf('All Departments')! > -1
        ? paramsDefault
        : paramsWithFilter;

    const response = await axios.post(
      `${this.backendUrl}/cost-explorer/costAndUsage`,
      params,
      configAxios,
    );

    const data: GetCostAndUsageCommandOutput = response.data;
    return data;
  }

  private async getDailyMetricByServiceCostExplorer(
    startDate?: string,
    endDate?: string,
    dept?: string,
  ): Promise<GetCostAndUsageCommandOutput> {
    const end = format(new Date(), 'yyyy-MM-dd');
    const duration = Duration.P3M;
    const start = inclusiveStartDateOf(duration, end);

    const paramsDefault = {
      Granularity: 'DAILY',
      GroupBy: [
        {
          Key: 'SERVICE',
          Type: 'DIMENSION',
        },
      ],
      Metrics: ['UnblendedCost'],
      TimePeriod: {
        End: endDate ?? end,
        Start: startDate ?? start,
      },
    };

    const paramsWithFilterService = {
      ...paramsDefault,
      Filter: {
        And: [
          {
            Dimensions: {
              Key: 'SERVICE',
              Values: [
                'AWS Amplify',
                'Amazon API Gateway',
                'Amazon AppStream',
                'AWS AppSync',
                'Amazon Athena',
                'CData AWS Glue Connector for Amazon DynamoDB',
                'AWS Certificate Manager',
                'Amazon Chime Voice Connector a service sold by AMCS LLC',
                'Amazon CloudFront',
                'AWS CloudShell',
                'AWS CloudTrail',
                'AmazonCloudWatch',
                'CloudWatch Events',
                'AWS CodeArtifact',
                'CodeBuild',
                'Amazon Cognito',
                'Amazon Comprehend',
                'Comprehend Medical',
                'AWS Config',
                'AWS Cost Explorer',
                'Amazon DevOps Guru',
                'AWS Direct Connect',
                'AWS Directory Service',
                'AWS Database Migration Service',
                'Amazon DynamoDB',
                'EC2 - Other',
                'Amazon EC2 Container Registry (ECR)',
                'Amazon Elastic Compute Cloud - Compute',
                'Amazon Elastic Container Service for Kubernetes',
                'Amazon Elastic File System',
                'Amazon Elastic Load Balancing',
                'Amazon ElastiCache',
                'AWS Entity Resolution',
                'AWS Global Accelerator',
                'AWS Glue',
                'Amazon GuardDuty',
                'Amazon Inspector',
                'AWS IoT',
                'AWS Key Management Service',
                'Amazon Kinesis',
                'Amazon Kinesis Analytics',
                'Amazon Kinesis Firehose',
                'AWS Lambda',
                'Amazon Lex',
                'Amazon Location Service',
                'Amazon Managed Grafana',
                'Amazon Managed Workflows for Apache Airflow',
                'AWS Migration Hub Refactor Spaces',
                'Amazon MQ',
                'Amazon Polly',
                'Refund',
                'Amazon Relational Database Service',
                'Amazon Route 53',
                'Amazon Simple Storage Service',
                'Amazon SageMaker',
                'AWS Secrets Manager',
                'AWS Security Hub',
                'AWS Service Catalog',
                'Amazon Simple Email Service',
                'Amazon Simple Workflow Service',
                'Amazon SimpleDB',
                'Amazon Simple Notification Service',
                'Amazon Simple Queue Service',
                'AWS Step Functions',
                'AWS Support (Developer)',
                'AWS Systems Manager',
                'Tax',
                'Amazon Transcribe',
                'Amazon Translate',
                'Amazon Virtual Private Cloud',
                'AWS WAF',
                'Amazon WorkDocs',
                'Amazon WorkSpaces',
                'Amazon WorkSpaces Thin Client',
                'AWS X-Ray',
              ],
            },
          },
          {
            Dimensions: {
              Key: 'SERVICE',
              Values: [
                'Amazon Elastic Container Service for Kubernetes',
                'Amazon Elastic Compute Cloud - Compute',
                'Amazon Relational Database Service',
                'AWS Config',
                'Comprehend Medical',
                'EC2 - Other',
                'Amazon ElastiCache',
                'AmazonCloudWatch',
                'Amazon Elastic Load Balancing',
                'Amazon API Gateway',
                'Amazon DynamoDB',
                'Amazon Kinesis',
                'AWS Security Hub',
                'AWS Certificate Manager',
                'AWS AppSync',
                'AWS Database Migration Service',
                'Amazon GuardDuty',
                'AWS Directory Service',
                'AWS WAF',
                'Amazon WorkSpaces Thin Client',
                'CloudWatch Events',
                'Amazon AppStream',
                'Amazon MQ',
                'AWS Lambda',
                'AWS X-Ray',
                'AWS Secrets Manager',
                'Amazon Elastic File System',
                // "Amazon Elastic Block Store"
              ],
            },
          },
        ],
      },
    };

    const filterTag: { Tags: { Key: string; Values: string[] } }[] = [];

    dept?.split(',').map(item => {
      if (item.trim() !== 'All Departments') {
        filterTag.push({ Tags: { Key: 'Dept', Values: [item.trim()] } });
      }
    });

    const paramsWithFilterDept =
      filterTag.length <= 1
        ? {
            ...paramsDefault,
            Filter: {
              Tags: {
                Key: 'Dept',
                Values: [dept],
              },
            },
          }
        : {
            ...paramsDefault,
            Filter: {
              Or: filterTag,
            },
          };

    const params =
      dept === null ||
      dept === undefined ||
      dept?.split(',').indexOf('All Departments')! > -1
        ? paramsWithFilterService
        : paramsWithFilterDept;

    const response = await axios.post(
      `${this.backendUrl}/cost-explorer/costAndUsage`,
      params,
      configAxios,
    );

    const data: GetCostAndUsageCommandOutput = response.data;
    return data;
  }

  private transformDataProductInsight(
    input: ResultByTime[],
    duration: Duration,
    aggregationByService: {
      id: string;
      aggregation: DateAggregation[];
    }[],
    product: string,
  ): Entity {
    const result: Entity = {
      id: product,
      aggregation: [0, 0],
      entities: { service: [] },
      change: { amount: 0 },
    };

    input.forEach(resultByTime => {
      const startDate = resultByTime.TimePeriod!.Start!;
      const amountOther: string =
        aggregationByService
          .find(agg => agg.id === 'Other')
          ?.aggregation.find(item => item.date === startDate)
          ?.amount.toString() ?? '0.00';

      resultByTime.Groups?.push({
        Keys: ['Other'],
        Metrics: {
          UnblendedCost: {
            Amount: amountOther,
            Unit: 'USD',
          },
        },
      });
    });

    const periodMapping: Record<Duration, number> = {
      P7D: 14,
      P30D: 60,
      P60D: 120,
      P15D: 30,
      P90D: 180,
      P3M: 180,
    };

    const durationDays: number = periodMapping[duration];
    const offsetDays: number = durationDays / 2;

    const today = new Date();

    input.forEach(resultByTime => {
      resultByTime.Groups?.forEach(group => {
        const [year, month, day] = resultByTime.TimePeriod!.Start!.split('-');
        const startDate = new Date(
          parseInt(year, 10),
          parseInt(month, 10) - 1,
          parseInt(day, 10),
        );

        const isWithinDuration = (date: Date): boolean => {
          const todayWithoutTime = new Date(
            today.getFullYear(),
            today.getMonth(),
            today.getDate(),
          );
          const dateWithoutTime = new Date(
            date.getFullYear(),
            date.getMonth(),
            date.getDate(),
          );
          const daysElapsed =
            Math.abs(todayWithoutTime.getTime() - dateWithoutTime.getTime()) /
            (1000 * 60 * 60 * 24);
          return parseInt(daysElapsed.toString(), 10) > offsetDays;
        };

        const serviceName = group!.Keys![0].replace('Amazon', '') ?? 'Service';
        const amount = parseFloat(group!.Metrics!.UnblendedCost.Amount ?? '0');

        const existingServiceIndex = result.entities.service.findIndex(
          service => service.id === serviceName,
        );

        if (existingServiceIndex === -1) {
          result.entities.service.push({
            id: serviceName,
            aggregation: [0, 0],
            entities: {},
            change: { amount: 0 },
          });
        }

        const service = result.entities.service.find(
          srv => srv.id === serviceName,
        )!;

        if (isWithinDuration(startDate)) {
          service.aggregation[0] += amount;
        } else {
          service.aggregation[1] += amount;
        }
      });
    });

    result.entities.service.forEach(service => {
      result.aggregation[0] += service.aggregation[0];
      result.aggregation[1] += service.aggregation[1];

      service.change.amount = service.aggregation[1] - service.aggregation[0];

      if (service.aggregation[0]) {
        service.change.ratio = service.aggregation[1]
          ? (service.aggregation[1] - service.aggregation[0]) /
            service.aggregation[0]
          : 0;
      }
    });

    result.change.amount = result.aggregation[1] - result.aggregation[0];
    result.change.ratio = result.aggregation[0]
      ? (result.aggregation[1] - result.aggregation[0]) / result.aggregation[0]
      : 0;

    return result;
  }
}
